import React, { useState, useEffect, useContext } from 'react';
import AnnonceContext from 'src/contexts/AnnonceContext';
import { SwatchesPicker } from 'react-color';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  IconButton, 
  Box, 
  Typography, 
  Stack,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import PaletteIcon from '@mui/icons-material/Palette';
import ColorLensIcon from '@mui/icons-material/ColorLens';

// Composant Dialog stylisé
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
    overflow: 'hidden',
  },
}));

// Composant DialogTitle stylisé
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: '#1b9faa',
  color: '#ffffff',
  padding: '20px 24px',
  '& .MuiTypography-root': {
    fontSize: '1.25rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
}));

// Composant DialogContent stylisé
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#fafafa',
}));

// Composant ColorButton stylisé
const ColorButton = styled(Box)(({ theme, selected, color }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '12px',
  backgroundColor: color,
  border: selected ? '3px solid #1b9faa' : '2px solid #e0e0e0',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
    borderColor: '#1b9faa',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
    transition: 'transform 0.3s ease',
    transform: 'translateX(-100%)',
  },
  '&:hover::before': {
    transform: 'translateX(100%)',
  },
}));

// Composant ColorVignette stylisé
const ColorVignette = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  padding: '8px',
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(27, 159, 170, 0.05)',
  },
}));

// Composant SwatchesPicker stylisé
const StyledSwatchesPicker = styled(Box)(({ theme }) => ({
  '& .swatches-picker': {
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  '& .swatches-picker > div': {
    borderRadius: '12px',
  },
}));

export default function Colorcheckbox({ data, parent, isSecret }) {
  const { formAttributes, setFormAttributes } = useContext(AnnonceContext);
  const [value, setValue] = useState(false);
  const [showColor, setShowColor] = useState(false);
  const [otherColor, setOtherColor] = useState("");
  const [open, setOpen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customColor, setCustomColor] = useState("#ffffff");

  useEffect(() => {
    const attr = formAttributes.filter((att) => att.attribute === parent.name);
    if (attr.length > 0) {
      setValue(attr[0].attributeValue);
      // Si c'est une couleur personnalisée, récupérer sa valeur
      if (attr[0].attributeValue === "Autre" && attr[0].uiValue) {
        setCustomColor(attr[0].uiValue);
      }
    }
  }, [formAttributes]);

  // Trouver la couleur sélectionnée
  const selectedColor = data.find((color) => color.name === value);

  const handleColorSelect = (color) => {
    if (color.name === "Autre") {
      setShowColorPicker(true);
      return;
    }
    
                  setFormAttributes((formAttributes) => [
                    ...formAttributes.filter((att) => att.attribute !== parent.name),
                    {
                      attribute: parent.name,
                      attributeValue: color.name,
                      isSecret: isSecret,
                      uiLabel: parent.uiLabel,
                      uiType: parent.ui,
                      uiValue: color.value,
                    },
                  ]);
                  setValue(color.name);
                  setOpen(false);
                  setShowColor(false);
  };

  const handleCustomColorChange = (color) => {
    setCustomColor(color.hex);
  };

  const handleCustomColorConfirm = () => {
    setFormAttributes((formAttributes) => [
      ...formAttributes.filter((att) => att.attribute !== parent.name),
      {
        attribute: parent.name,
        attributeValue: "Autre",
        isSecret: isSecret,
        uiLabel: parent.uiLabel,
        uiType: parent.ui,
        uiValue: customColor,
      },
    ]);
    setValue("Autre");
    setShowColorPicker(false);
    setOpen(false);
  };

  // Vérifier si l'option "Autre" existe
  const hasOtherOption = data.some(color => color.name === "Autre");

  // Déterminer la couleur à afficher
  const getDisplayColor = () => {
    if (value === "Autre") {
      return customColor;
    }
    return selectedColor ? selectedColor.value : '#f5f5f5';
  };

  // Déterminer le nom à afficher
  const getDisplayName = () => {
    if (value === "Autre") {
      return "Couleur personnalisée";
    }
    return selectedColor ? selectedColor.name : "";
  };

  // Déterminer la valeur à afficher dans le chip
  const getDisplayValue = () => {
    if (value === "Autre") {
      return customColor;
    }
    return selectedColor ? selectedColor.value : "";
  };

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <ColorButton
          selected={!!value}
          color={getDisplayColor()}
          onClick={() => setOpen(true)}
          sx={{
            width: '50px',
            height: '50px',
            borderRadius: '12px',
            border: value ? '3px solid #1b9faa' : '2px solid #e0e0e0',
            backgroundColor: getDisplayColor(),
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          }}
        >
          {!value && (
            <PaletteIcon sx={{ color: '#999', fontSize: '1.5rem' }} />
          )}
          {value && (
            <CheckIcon sx={{ color: '#ffffff', fontSize: '1.2rem', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }} />
          )}
        </ColorButton>
        

      </Box>

      {/* Dialog principal pour les couleurs prédéfinies */}
      <StyledDialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <StyledDialogTitle>
          <PaletteIcon />
          Choisir une couleur
        </StyledDialogTitle>
        <StyledDialogContent>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
            gap: 2,
            padding: 2,
          }}>
            {data.map((color, index) => (
              <ColorVignette key={index}>
                <ColorButton
                  selected={value === color.name}
                  color={color.value}
                  onClick={() => handleColorSelect(color)}
                  sx={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    border: value === color.name ? '3px solid #1b9faa' : '2px solid #e0e0e0',
                    backgroundColor: color.value,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  {color.name === "Autre" ? (
                    <ColorLensIcon 
                      sx={{ 
                        color: '#ffffff', 
                        fontSize: '1.5rem',
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
                      }} 
                    />
                  ) : value === color.name && (
                    <CheckIcon 
                      sx={{ 
                        color: '#ffffff', 
                        fontSize: '1.5rem',
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
                      }} 
                    />
                  )}
                </ColorButton>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    textAlign: 'center',
                    fontWeight: value === color.name ? 600 : 400,
                    color: value === color.name ? '#1b9faa' : '#666',
                    fontSize: '0.75rem',
                    lineHeight: 1.2,
                  }}
                >
                  {color.name === "Autre" ? "Autre..." : color.name}
                </Typography>
              </ColorVignette>
            ))}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button 
              variant="outlined" 
              onClick={() => setOpen(false)}
              sx={{ 
                borderRadius: '8px',
                borderColor: '#1b9faa',
                color: '#1b9faa',
                '&:hover': {
                  borderColor: '#158a94',
                  backgroundColor: 'rgba(27, 159, 170, 0.05)',
                },
              }}
            >
              Fermer
            </Button>
          </Box>
        </StyledDialogContent>
      </StyledDialog>

      {/* Dialog pour le nuancier personnalisé */}
      <StyledDialog 
        open={showColorPicker} 
        onClose={() => setShowColorPicker(false)}
        maxWidth="sm"
        fullWidth
      >
        <StyledDialogTitle>
          <ColorLensIcon />
          Choisir une couleur personnalisée
        </StyledDialogTitle>
        <StyledDialogContent>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <StyledSwatchesPicker>
                <SwatchesPicker
                  color={customColor}
                  onChange={handleCustomColorChange}
                  width={320}
                  height={200}
                />
              </StyledSwatchesPicker>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 2,
              p: 2,
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Couleur sélectionnée :
              </Typography>
              <Box
                sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  backgroundColor: customColor,
                  border: '2px solid #e0e0e0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              />
              <Chip 
                label={customColor} 
                size="small" 
                sx={{ 
                  backgroundColor: customColor,
                  color: '#ffffff',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }} 
              />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => setShowColorPicker(false)}
                sx={{ 
                  borderRadius: '8px',
                  borderColor: '#666',
                  color: '#666',
                  '&:hover': {
                    borderColor: '#333',
                    backgroundColor: 'rgba(0,0,0,0.05)',
                  },
                }}
              >
                Annuler
              </Button>
              <Button 
                variant="contained" 
                onClick={handleCustomColorConfirm}
                sx={{ 
                  borderRadius: '8px',
                  backgroundColor: '#1b9faa',
                  '&:hover': {
                    backgroundColor: '#158a94',
                  },
                }}
              >
                Confirmer
              </Button>
            </Box>
          </Stack>
        </StyledDialogContent>
      </StyledDialog>
    </Stack>
  );
}
